import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isLunchItem } from "../../../../../lib/lunchCatalog";
import { Modal } from "antd";
import { useOrderingMessage } from "../../useOrderingMessage";
import { AudioOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../../../hooks/useViewport";
import {
  useEnabledItems,
  useMenuAddons,
  useMenuModifiers,
} from "../../../../../hooks/useMenu";
import { useBills, useCreateBill } from "../../../../../hooks/useBills";
import { useAppDispatch } from "../../../../../store/hooks";
import { setSelectedBillId } from "../../../../../store";
import {
  getApiErrorMessage,
  getItemName,
  getTranslation,
} from "../../../../../utils/translations";
import {
  parseVoiceOrder,
  transcribeVoice,
  voiceErrorKey,
  type ParseVoiceOrderOut,
} from "../../../../../api/voiceOrders";
import {
  ModalHeader,
  ModalFooter,
  GhostButton,
  PrimaryButton,
  posModalStyles as modalStyles,
} from "../../modalPrimitives";
import { EditModifiersModal } from "../../menu/EditModifiersModal";
import { useAudioRecorder } from "../../../../../hooks/useAudioRecorder";
import type { VoiceSeatOutcome } from "./useVoiceSeat";
import {
  addonOptionPrice,
  buildVoiceElements,
  resolveVoiceRows,
  voiceTargetBill,
  type ResolvedVoiceRow,
  type VoiceRowConfig,
} from "./voiceOrderResolution";
import {
  ChooseStep,
  ErrorStep,
  ListeningStep,
  ParsingStep,
  PreviewStep,
} from "./VoiceOrderSteps";
import { useReservationConfirm } from "../manage/useReservationConfirm";

// ЕДИНСТВЕНИЯТ модал на гласовата поръчка — прозорецът + целият „мозък":
// държи state-а, ефектите и двете заявки към BE. Тялото му е слайд според
// текущата стъпка (компонентите в VoiceOrderSteps.tsx — те са ЧИСТО
// презентационни, без state и без заявки; кликовете им се връщат тук през
// callbacks и решенията се взимат тук).
//
// Поток: запис push-to-talk — кръгъл бутон, който записва микрофона САМО
// докато е натиснат (useAudioRecorder; пускането на бутона спира записа) →
// транскрипция (POST /voice/transcribe — звукът става текст на сървъра, в
// holdEnd) → парсване (POST /voice/parse-order, в runParse) → „choose" при
// двусмислия → преглед (непокрити задължителни добавки/модификатори отварят
// вградения EditModifiersModal) → POST /bills (последната заявка, в handleConfirm).
//
// ЗАЩО звукът отива на сървъра, а не се разпознава в браузъра: Web Speech
// API работи стабилно само в Chrome за десктоп — липсва в Chrome за iPhone
// и в инсталираното iOS PWA, а в Android Chrome спира сам след пауза.
// MediaRecorder + сървърна транскрипция е един и същ път на всички устройства.
//
// Принцип на state-а: суровият отговор на модела (parsed) НЕ се променя;
// всичко, което потребителят уточнява (избори при двусмислие, довършени
// редове от „Промени"), се трупа в ОТДЕЛНИ речници и се комбинира с parsed
// на всяко рендиране в memo-то rows (resolveVoiceRows).

// Стъпките на модала. Преходи: отваряне → listening (ИЗЧАКВА натискане на
// push-to-talk бутона, микрофонът НЕ тръгва сам); пускане на бутона със
// запис → transcribing → parsing; отговор от BE → choose (ефект прескача
// веднага към preview, ако няма нищо за избиране); грешка/тишина/отказан
// микрофон → error; „Отново" връща към listening отвсякъде.
type VoiceStep =
  | "listening"
  | "transcribing"
  | "parsing"
  | "choose"
  | "preview"
  | "error";

interface VoiceOrderModalProps {
  open: boolean;
  onClose: () => void;
  // Лична номерация: „отвори маса N" търси само в МОИТЕ маси (като „Нова маса").
  personalNumberingEnabled: boolean;
  currentUserId: number | null;
  // „Настани резервацията на …" (`useVoiceSeat`); null — без правото „Настани".
  // `isCurrent` — диалогът е още на същото слушане (не е затворен, не е
  // „Отново", не е демонтиран); остарял резултат не настанява и не навигира.
  onSeatReservation: ((spokenName: string, isCurrent: () => boolean) => Promise<VoiceSeatOutcome>) | null;
}

export const VoiceOrderModal = ({
  open,
  onClose,
  personalNumberingEnabled,
  currentUserId,
  onSeatReservation,
}: VoiceOrderModalProps) => {
  const { t, i18n } = useTranslation();
  const { isNarrow, isDesktop } = useViewport();
  const message = useOrderingMessage();
  const dispatch = useAppDispatch();
  // Гласът поръчва от основното меню — копията от обедното (виж
  // lib/lunchCatalog.ts) носят синтетични id-та и не влизат в разпознаването.
  const enabledItems = useEnabledItems();
  const allItems = useMemo(
    () => enabledItems.filter((item) => !isLunchItem(item)),
    [enabledItems],
  );
  const allAddons = useMenuAddons();
  const allModifiers = useMenuModifiers();
  const { data: bills = [] } = useBills({ fromCacheOnly: true });
  const createBillMutation = useCreateBill();
  // „отвори маса N" на маса с резервация → „Продължаваш ли?" (Отказ/Продължи)
  const { confirmTable, reservationConfirmModal } = useReservationConfirm();

  const [step, setStep] = useState<VoiceStep>("listening");
  const [errorText, setErrorText] = useState("");
  // Суровият отговор на модела — след получаване НЕ се модифицира
  const [parsed, setParsed] = useState<ParseVoiceOrderOut | null>(null);
  // Изборите при двусмислие: продукт per ред (индекс на реда → productId);
  // модификатор/опция per choiceKey ("ред:m0" / "ред:a0" — виж
  // voiceOrderResolution.ts за ключовете)
  const [chosenProductIds, setChosenProductIds] = useState<
    Record<number, number>
  >({});
  const [chosenModifierIds, setChosenModifierIds] = useState<
    Record<string, number>
  >({});
  const [chosenAddonOptions, setChosenAddonOptions] = useState<
    Record<string, string>
  >({});
  // Финална конфигурация от „Промени" per ред — когато я има, тя БИЕ
  // гласовата интерпретация на реда (виж resolveVoiceRows)
  const [rowOverrides, setRowOverrides] = useState<
    Record<number, VoiceRowConfig>
  >({});
  // Редът, за който е отворен „Промени" (EditModifiersModal; null = затворен)
  const [configureRowIndex, setConfigureRowIndex] = useState<number | null>(
    null,
  );
  // Транскриптът, с който е пуснато парсването (за показване в прегледа)
  const [parsedTranscript, setParsedTranscript] = useState("");
  // Името на масата е РЕДАКТИРУЕМО в прегледа: предпопълва се с чутото от
  // модела, но потребителят може да го смени (заета маса от друг оператор)
  // или да го напише от нулата (ако маса не е спомената изобщо).
  const [tableNameInput, setTableNameInput] = useState("");

  const { supported, recording, elapsedSeconds, start, stop, cancel } =
    useAudioRecorder();
  // Номер на текущото „слушане": „Отново"/затваряне/демонтаж го увеличават, а
  // holdEnd и runParse го сверяват след всяко await, за да не пише резултат
  // от стар запис върху ново състояние (и да не настанява след затваряне).
  const listenSessionRef = useRef(0);
  useEffect(
    () => () => {
      listenSessionRef.current += 1;
    },
    [],
  );

  const itemById = useMemo(() => {
    const map = new Map<number, (typeof allItems)[number]>();
    for (const item of allItems) map.set(item.id, item);
    return map;
  }, [allItems]);

  const addonById = useMemo(() => {
    const map = new Map<number, (typeof allAddons)[number]>();
    for (const addon of allAddons) map.set(addon.id, addon);
    return map;
  }, [allAddons]);

  const modifierById = useMemo(() => {
    const map = new Map<number, (typeof allModifiers)[number]>();
    for (const modifier of allModifiers) map.set(modifier.id, modifier);
    return map;
  }, [allModifiers]);

  const productName = useCallback(
    (productId: number) => {
      const item = itemById.get(productId);
      return item
        ? getItemName(item.itemLangs, i18n.language, item.slug)
        : `#${productId}`;
    },
    [itemById, i18n.language],
  );

  const modifierName = useCallback(
    (modifierId: number) => {
      const modifier = modifierById.get(modifierId);
      return modifier
        ? getTranslation(modifier.translations, i18n.language, modifier.slug)
        : `#${modifierId}`;
    },
    [modifierById, i18n.language],
  );

  const addonName = useCallback(
    (addonId: number) => {
      const addon = addonById.get(addonId);
      return addon
        ? getTranslation(addon.translations, i18n.language, addon.slug)
        : `#${addonId}`;
    },
    [addonById, i18n.language],
  );

  // Менюто, което заминава към BE (и оттам в промпта на модела): продукти с
  // връзките им + речници на модификаторите и групите добавки.
  //
  // ЗАЩО FE го праща, а BE не си го зарежда сам: (а) GET /menu вече е свършил
  // warehouse-mode разклоняването (артикули vs рецепти), преводите и enabled
  // филтрите — гласовият service остава режимно-сляп; (б) кандидат-id-тата,
  // които моделът върне, гарантирано съществуват в менюто, което FE показва и
  // поръчва — двете снимки не могат да се разминат; (в) имената са на
  // текущия език на потребителя. Цената е по-голям payload — приемливо.
  const menuPayload = useMemo(() => {
    const products = allItems.map((item) => ({
      id: item.id,
      name: getItemName(item.itemLangs, i18n.language, item.slug),
      modifierIds: item.modifierIds,
      addonIds: item.addonIds,
    }));
    const modifiers = allModifiers.map((modifier) => ({
      id: modifier.id,
      name: getTranslation(modifier.translations, i18n.language, modifier.slug),
    }));
    const addons = allAddons.map((addon) => ({
      id: addon.id,
      name: getTranslation(addon.translations, i18n.language, addon.slug),
      type: String(addon.type),
      options: addon.items.flatMap((addonItem) => {
        const optionItem = itemById.get(addonItem.itemId);
        return optionItem
          ? [
              {
                itemId: addonItem.itemId,
                name: getItemName(
                  optionItem.itemLangs,
                  i18n.language,
                  optionItem.slug,
                ),
              },
            ]
          : [];
      }),
    }));
    return { products, modifiers, addons };
  }, [allItems, allModifiers, allAddons, itemById, i18n.language]);

  // Имената от менюто като подсказка към транскрипцията (продуктите първи —
  // BE-то реже над 500; модификаторите „без лед"/„малка" след тях). Без
  // дубликати, за да не хабим лимита.
  const voiceKeywords = useMemo(() => {
    const seen = new Set<string>();
    const keywords: string[] = [];
    for (const name of [
      ...menuPayload.products.map((product) => product.name),
      ...menuPayload.modifiers.map((modifier) => modifier.name),
    ]) {
      const trimmed = name.trim();
      const key = trimmed.toLowerCase();
      if (!trimmed || seen.has(key)) continue;
      seen.add(key);
      keywords.push(trimmed);
    }
    return keywords;
  }, [menuPayload]);

  // Връща модала в изходната стъпка „слушане" (пълен reset), БЕЗ да пуска
  // микрофона — той тръгва чак при натискане на push-to-talk бутона.
  const beginListening = useCallback(() => {
    setStep("listening");
    setParsed(null);
    setChosenProductIds({});
    setChosenModifierIds({});
    setChosenAddonOptions({});
    setRowOverrides({});
    setConfigureRowIndex(null);
    setTableNameInput("");
    setErrorText("");
    listenSessionRef.current += 1;
    cancel();
  }, [cancel]);

  // Push-to-talk: натискане на кръглия бутон = старт на записа, пускане =
  // стоп → holdEnd праща записа за транскрипция и после за парсване.
  const holdStart = useCallback(() => {
    if (recording) return;
    start();
  }, [recording, start]);

  // Отваряне на модала → изходната стъпка (микрофонът чака натискане)
  useEffect(() => {
    if (!open) return;
    if (!supported) {
      setStep("error");
      setErrorText(t("ordering.voiceNotSupported"));
      return;
    }
    beginListening();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Затваряне по средата на запис → освобождаваме микрофона (важно за iOS
  // PWA: незатворен стрийм блокира следващия запис)
  useEffect(() => {
    if (!open) {
      listenSessionRef.current += 1;
      cancel();
    }
  }, [open, cancel]);

  // ЕДИНСТВЕНОТО място, което вика POST /voice/parse-order — веднъж на
  // слушане (повторна заявка има само след „Отново" → ново слушане).
  // Всичко след отговора (избори, „Промени", суми) е чисто FE върху parsed.
  // `session` е слушането, от което идва текстът: затворен диалог или
  // „Отново" по време на заявката правят отговора остарял — той не сменя
  // стъпката/грешката, не настанява и не затваря новия диалог.
  const runParse = useCallback(
    async (spokenText: string, session: number) => {
      const isCurrent = () => listenSessionRef.current === session;
      setStep("parsing");
      setParsedTranscript(spokenText);
      try {
        const result = await parseVoiceOrder(
          spokenText,
          menuPayload.products,
          menuPayload.modifiers,
          menuPayload.addons,
        );
        if (!isCurrent()) return;
        // „Настани резервацията на …" — не е поръчка: ясният случай настанява
        // направо, иначе се отваря „Настани" за ръчно довършване (useVoiceSeat).
        if (result.seatGuestName?.trim()) {
          if (!onSeatReservation) {
            setStep("error");
            setErrorText(t("ordering.voiceSeatNotAllowed"));
            return;
          }
          const outcome = await onSeatReservation(result.seatGuestName, isCurrent);
          if (!isCurrent() || outcome.kind === "cancelled") return;
          if (outcome.kind === "error") {
            setStep("error");
            setErrorText(outcome.text);
            return;
          }
          onClose();
          return;
        }
        if (result.items.length === 0) {
          setStep("error");
          setErrorText(t("ordering.voiceNoItems"));
          return;
        }
        setParsed(result);
        setTableNameInput(result.tableName?.trim() ?? "");
        // „choose" винаги — ефектът долу прескача към прегледа, ако няма избори
        setStep("choose");
      } catch (err: unknown) {
        if (!isCurrent()) return;
        // BE-то праща машинен код (502 → detail.code), не готов текст —
        // съобщението се превежда тук, за да е на езика на терминала.
        setStep("error");
        setErrorText(t(voiceErrorKey(err)));
      }
    },
    [menuPayload, t, onSeatReservation, onClose],
  );

  // Пускане на бутона (pointerup/pointercancel) → край на записа → звукът
  // отива за транскрипция, текстът — за парсване. Кратко натискане или
  // пускане преди разрешението за микрофона („empty") оставя модала в
  // изходната стъпка да чака ново натискане. Второ извикване за същия
  // запис (pointerup И pointercancel) получава „empty" от hook-а и излиза.
  const holdEnd = useCallback(async () => {
    const session = listenSessionRef.current;
    const outcome = await stop();
    if (listenSessionRef.current !== session) return;
    if (outcome.kind === "empty") return;
    if (outcome.kind === "error") {
      setStep("error");
      setErrorText(
        t(
          outcome.error === "denied"
            ? "ordering.voiceMicDenied"
            : "ordering.voiceMicUnavailable",
        ),
      );
      return;
    }

    setStep("transcribing");
    let transcript: string;
    try {
      transcript = await transcribeVoice(
        outcome.audio.blob,
        outcome.audio.mimeType,
        voiceKeywords,
      );
    } catch (err: unknown) {
      if (listenSessionRef.current !== session) return;
      setStep("error");
      setErrorText(t(voiceErrorKey(err)));
      return;
    }
    if (listenSessionRef.current !== session) return;
    if (!transcript.trim()) {
      setStep("error");
      setErrorText(t("ordering.voiceNoSpeech"));
      return;
    }
    void runParse(transcript.trim(), session);
  }, [stop, runParse, voiceKeywords, t]);

  // СЪРЦЕТО на извеждането: комбинира неизменния parsed + изборите на
  // потребителя → готови редове (конфигурация, висящи избори, needsConfig,
  // суми). Преизчислява се при всяка промяна на който и да е вход — затова
  // кликовете в ChooseStep/„Промени" се отразяват навсякъде веднага.
  const rows = useMemo(() => {
    if (!parsed) return [];
    return resolveVoiceRows({
      parsed,
      chosenProductIds,
      chosenModifierIds,
      chosenAddonOptions,
      rowOverrides,
      itemById,
      addonById,
      modifierById,
    });
  }, [
    parsed,
    chosenProductIds,
    chosenModifierIds,
    chosenAddonOptions,
    rowOverrides,
    itemById,
    addonById,
    modifierById,
  ]);

  const ambiguousProducts = useMemo(() => {
    if (!parsed) return [];
    return parsed.items
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => item.productIds.length > 1);
  }, [parsed]);

  const allProductsChosen = ambiguousProducts.every(
    ({ index }) => chosenProductIds[index] != null,
  );
  const pendingDetailChoices = rows.flatMap((row) => [
    ...row.pendingModifierChoices,
    ...row.pendingAddonChoices,
  ]);
  const allChoicesResolved =
    allProductsChosen && pendingDetailChoices.length === 0;

  // Нищо за избиране → прескачаме направо към прегледа
  useEffect(() => {
    if (step === "choose" && parsed && allChoicesResolved) setStep("preview");
  }, [step, parsed, allChoicesResolved]);

  // validRows = редове с намерен продукт (неразпознатите се показват
  // зачертани, но НЕ влизат в поръчката); rowsNeedingConfig блокират
  // „Поръчай", докато не се довършат през „Промени".
  const validRows = rows.filter(
    (row) => row.menuItem && row.config.quantity > 0,
  );
  const rowsNeedingConfig = validRows.filter((row) => row.needsConfig);
  const orderTotal = validRows.reduce((sum, row) => sum + row.lineTotal, 0);

  // Ефективното име идва от РЕДАКТИРУЕМОТО поле (не директно от модела)
  const tableName = tableNameInput.trim() || null;
  // Отворена сметка със същото име → допоръчка към нея вместо нова маса
  // (при лична номерация — само моята, `voiceTargetBill`).
  const existingBill = useMemo(
    () => (tableName ? voiceTargetBill(bills, tableName, personalNumberingEnabled, currentUserId) : null),
    [bills, tableName, personalNumberingEnabled, currentUserId],
  );

  const configureRow =
    configureRowIndex != null
      ? (rows.find((row) => row.index === configureRowIndex) ?? null)
      : null;

  // Втората (и последна) заявка: POST /bills — СЪЩИЯТ endpoint като ръчното
  // маркиране; BE не знае, че поръчката идва от глас. Отворена маса със
  // същото име → допоръчка (billId); няма → нова маса (tableName). Успехът
  // инвалидира ["bills"] (списъкът с маси се опреснява сам) и селектира
  // сметката през Redux.
  const handleConfirm = async () => {
    if (!tableName || validRows.length === 0 || rowsNeedingConfig.length > 0)
      return;
    if (createBillMutation.isPending) return;
    // Нова маса (не допоръчка) с резервация — питаме; „Отказ" оставя прегледа.
    if (!existingBill && !(await confirmTable(tableName))) return;

    try {
      const result = await createBillMutation.mutateAsync({
        billId: existingBill?.id ?? 0,
        tableName: existingBill ? undefined : tableName,
        elements: buildVoiceElements(validRows, addonById, itemById),
        comment: "",
      });
      message.success(t("ordering.voiceSuccess", { name: tableName }));
      dispatch(setSelectedBillId(result.id));
      onClose();
    } catch (err: unknown) {
      message.error(getApiErrorMessage(err, t("common.error")));
    }
  };

  const fmtPrice = (value: number) => `${value.toFixed(2)} €`;

  // Ref към актуалния handleConfirm — Enter ефектът го вика, без да влачи
  // всичките му зависимости (същият шаблон като submitOrderRef в Ordering.tsx).
  const confirmRef = useRef(handleConfirm);
  confirmRef.current = handleConfirm;

  // Enter = „Поръчай" в прегледа (десктоп): гейтовете на handleConfirm важат
  // и тук (маса/валидни редове/недовършени), затова няма втора проверка.
  // Не и при отворен „Промени" (той си има собствен Enter) или фокус в поле.
  useEffect(() => {
    if (!open || step !== "preview" || !isDesktop) return;
    if (configureRowIndex != null) return;

    const handleEnter = (event: KeyboardEvent) => {
      if (event.key !== "Enter") return;
      if (
        event.target instanceof HTMLElement &&
        (event.target.tagName === "INPUT" ||
          event.target.tagName === "TEXTAREA")
      )
        return;
      event.preventDefault();
      void confirmRef.current();
    };
    window.addEventListener("keydown", handleEnter);
    return () => window.removeEventListener("keydown", handleEnter);
  }, [open, step, isDesktop, configureRowIndex]);

  // Esc = „Отказ" (затваря модала) — собствен handler вместо antd keyboard
  // (Modal е с keyboard={false}, за да няма двойно затваряне). Не и при
  // отворен „Промени" — той си има собствен Esc; preventDefault пази
  // глобалната Esc-навигация (SharedAccountGate) от същото натискане.
  useEffect(() => {
    if (!open || configureRowIndex != null) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onClose();
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [open, configureRowIndex, onClose]);

  /** Детайлите на реда за прегледа — в СЪЩИЯ вид като кошницата/детайла на
      маса: добавките като chips с финално количество × цена (тип 1 е
      абсолютно, тип 2/3 × бройката), модификаторите по име, коментарът. */
  const rowDetails = useCallback(
    (row: ResolvedVoiceRow) => {
      const addonChips: { name: string; qty: number; price: number }[] = [];
      for (const [addonIdText, qtyMap] of Object.entries(row.config.addons)) {
        const addon = addonById.get(Number(addonIdText));
        const absolute = String(addon?.type) === "1";
        for (const [itemIdText, qty] of Object.entries(qtyMap)) {
          const optionItemId = Number(itemIdText);
          addonChips.push({
            name: productName(optionItemId),
            qty: absolute ? qty : qty * row.config.quantity,
            price: addon ? addonOptionPrice(addon, optionItemId) : 0,
          });
        }
      }
      return {
        addons: addonChips,
        modifiers: row.config.modifiers.map(modifierName),
        comment: row.config.comment,
      };
    },
    [addonById, modifierName, productName],
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      keyboard={false}
      centered
      width={isNarrow ? "calc(100vw - 24px)" : 560}
      styles={modalStyles}
      destroyOnHidden
    >
      <ModalHeader
        icon={<AudioOutlined />}
        title={t("ordering.voiceOrder")}
        subtitle={
          step === "preview" && parsedTranscript
            ? `„${parsedTranscript}“`
            : undefined
        }
        onClose={onClose}
        isNarrow={isNarrow}
      />

      {/* Тяло по стъпка */}
      {step === "listening" && (
        <ListeningStep
          recording={recording}
          elapsedSeconds={elapsedSeconds}
          onHoldStart={holdStart}
          onHoldEnd={() => void holdEnd()}
        />
      )}
      {step === "transcribing" && <ParsingStep transcript={null} />}
      {step === "parsing" && <ParsingStep transcript={parsedTranscript} />}
      {step === "error" && <ErrorStep text={errorText} />}
      {step === "choose" && parsed && (
        <ChooseStep
          ambiguousProducts={ambiguousProducts}
          rows={rows}
          chosenProductIds={chosenProductIds}
          chosenModifierIds={chosenModifierIds}
          chosenAddonOptions={chosenAddonOptions}
          onChooseProduct={(rowIndex, productId) =>
            setChosenProductIds((previous) => ({
              ...previous,
              [rowIndex]: productId,
            }))
          }
          onChooseModifier={(choiceKey, modifierId) =>
            setChosenModifierIds((previous) => ({
              ...previous,
              [choiceKey]: modifierId,
            }))
          }
          onChooseAddonOption={(choiceKey, value) =>
            setChosenAddonOptions((previous) => ({
              ...previous,
              [choiceKey]: value,
            }))
          }
          productName={productName}
          modifierName={modifierName}
          addonName={addonName}
          addonById={addonById}
          fmtPrice={fmtPrice}
          itemPrice={(productId) => itemById.get(productId)?.price ?? null}
        />
      )}
      {step === "preview" && parsed && (
        <PreviewStep
          rows={rows}
          tableNameInput={tableNameInput}
          onTableNameChange={setTableNameInput}
          onSubmit={handleConfirm}
          isExistingBill={existingBill != null}
          orderTotal={orderTotal}
          productName={productName}
          rowDetails={rowDetails}
          fmtPrice={fmtPrice}
          onConfigureRow={setConfigureRowIndex}
        />
      )}

      {/* Футър по стъпка */}
      <ModalFooter isNarrow={isNarrow}>
        <GhostButton
          label={t("ordering.voiceCancel")}
          onClick={onClose}
          shortcut={isDesktop ? "Esc" : undefined}
        />
        {(step === "error" || step === "preview" || step === "choose") &&
          supported && (
            <GhostButton
              label={t("ordering.voiceRetry")}
              icon={<AudioOutlined />}
              onClick={beginListening}
            />
          )}
        {step === "choose" && (
          <PrimaryButton
            label={t("ordering.voiceContinue")}
            onClick={() => setStep("preview")}
            disabled={!allChoicesResolved}
          />
        )}
        {step === "preview" && (
          <PrimaryButton
            label={`${t("ordering.voiceConfirm")} · ${fmtPrice(orderTotal)}`}
            shortcut={isDesktop ? "⏎" : undefined}
            onClick={handleConfirm}
            disabled={
              !tableName ||
              validRows.length === 0 ||
              rowsNeedingConfig.length > 0 ||
              createBillMutation.isPending
            }
            loading={createBillMutation.isPending}
          />
        )}
      </ModalFooter>

      {/* „Промени" за ред от гласовата поръчка — префилнат с гласовия избор;
          потвърждението замества конфигурацията на реда (rowOverrides). */}
      <EditModifiersModal
        item={configureRow?.menuItem ?? null}
        open={configureRow != null}
        isNewLine
        confirmLabelKey="ordering.modifyDone"
        onClose={() => setConfigureRowIndex(null)}
        onSave={(data) => {
          if (configureRowIndex == null) return;
          setRowOverrides((previous) => ({
            ...previous,
            [configureRowIndex]: data,
          }));
        }}
        initial={configureRow?.config ?? null}
      />
      {reservationConfirmModal}
    </Modal>
  );
};
